import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Global progress bar + spinning Brivo logo loader.
 * Driven by:
 *   - route changes (auto)
 *   - axios request/response interceptors (via startProgress/stopProgress)
 *
 * Exposes a tiny global pub/sub so non-React modules (axios interceptors)
 * can drive it without imports.
 */

const BUS = (() => {
  if (typeof window === "undefined") return null;
  if (!window.__brivoProgress) {
    const listeners = new Set();
    window.__brivoProgress = {
      active: 0,
      start() {
        this.active += 1;
        listeners.forEach((l) => l(this.active));
      },
      stop() {
        this.active = Math.max(0, this.active - 1);
        listeners.forEach((l) => l(this.active));
      },
      reset() {
        this.active = 0;
        listeners.forEach((l) => l(0));
      },
      subscribe(fn) {
        listeners.add(fn);
        return () => listeners.delete(fn);
      },
    };
  }
  return window.__brivoProgress;
})();

export const startProgress = () => BUS && BUS.start();
export const stopProgress = () => BUS && BUS.stop();
export const resetProgress = () => BUS && BUS.reset();

const LOGO_URL = "/brivo-logo.png";

/**
 * Floating spinning Brivo logo + thin top progress bar.
 * Renders globally, fixed top-right corner, on every page.
 */
export default function PageLoader() {
  const location = useLocation();
  const [active, setActive] = useState(0);
  const [routeBump, setRouteBump] = useState(0);
  const timerRef = useRef(null);

  // Subscribe to axios bus
  useEffect(() => {
    if (!BUS) return;
    setActive(BUS.active);
    return BUS.subscribe(setActive);
  }, []);

  // Trigger short pulse on every route change
  useEffect(() => {
    setRouteBump((n) => n + 1);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setRouteBump(0), 700);
    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const visible = active > 0 || routeBump > 0;
  const indeterminate = active > 0;

  return (
    <>
      {/* Global keyframes (always available — used by FullPageLoader/SectionLoader too) */}
      <style>{`
        @keyframes brivoSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes brivoFill { 0% { transform: scaleX(0); } 50% { transform: scaleX(0.6); } 100% { transform: scaleX(1); } }
        @keyframes brivoIndet { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }
        @keyframes brivoPop { 0% { transform: scale(0.6); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
        .brivo-spin { animation: brivoSpin 1.1s linear infinite; }
        .brivo-fill { animation: brivoFill 0.7s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
        .brivo-indet { animation: brivoIndet 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
        .brivo-pop { animation: brivoPop 0.18s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
      `}</style>

      {/* Top progress bar */}
      {visible && (
        <div
          data-testid="page-loader"
          className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none h-[3px] overflow-hidden bg-transparent"
          aria-hidden="true"
        >
          <div
            className={
              indeterminate
                ? "h-full w-2/5 bg-[#1fa868] shadow-[0_0_10px_#1fa868] brivo-indet"
                : "h-full bg-[#1fa868] shadow-[0_0_10px_#1fa868] brivo-fill origin-left"
            }
          />
        </div>
      )}

      {/* Spinning logo (centered) — only while requests in-flight */}
      {indeterminate && (
        <div
          data-testid="page-loader-logo"
          className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center brivo-pop"
          aria-hidden="true"
        >
          <div className="w-20 h-20 rounded-2xl bg-black/85 backdrop-blur-sm shadow-[0_16px_48px_rgba(0,0,0,0.35)] flex items-center justify-center">
            <img
              src={LOGO_URL}
              alt=""
              className="w-12 h-12 brivo-spin select-none"
              draggable={false}
            />
          </div>
        </div>
      )}
    </>
  );
}

/** Full-screen overlay loader — for app boot or heavy operations. */
export function FullPageLoader({ label = "Loading" }) {
  return (
    <div
      data-testid="full-page-loader"
      className="fixed inset-0 z-[9998] bg-white/85 backdrop-blur-sm flex items-center justify-center"
    >
      <div className="flex flex-col items-center gap-5">
        <div className="w-20 h-20 rounded-2xl bg-black/85 shadow-[0_12px_40px_rgba(0,0,0,0.3)] flex items-center justify-center">
          <img
            src={LOGO_URL}
            alt="Brivo"
            className="w-14 h-14 brivo-spin select-none"
            draggable={false}
          />
        </div>
        <div
          data-testid="full-page-loader-label"
          className="text-xs font-bold text-black/70 tracking-[0.3em] uppercase"
        >
          {label}
        </div>
      </div>
    </div>
  );
}

/** Inline section loader — drop into a card / panel while it fetches. */
export function SectionLoader({ label, className = "" }) {
  return (
    <div
      data-testid="section-loader"
      className={`flex flex-col items-center justify-center gap-3 py-10 text-sm text-black/60 ${className}`}
    >
      <img
        src={LOGO_URL}
        alt=""
        className="w-9 h-9 brivo-spin select-none opacity-90"
        draggable={false}
      />
      {label || "Loading…"}
    </div>
  );
}
